import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, Star, Zap, Crown, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map plan id (UI) → backend SKU + tier
const PLAN_SKU_MAP = {
  premium: { sku: 'premium_buyer', role: 'user', tier: 'premium' },
  independent: { sku: 'agent_independent', role: 'agent', tier: 'independent' },
  pro: { sku: 'agent_pro', role: 'agent', tier: 'pro' },
};

export default function UpgradePage() {
  const { user, token, refreshUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [skus, setSkus] = useState(null);
  const [processing, setProcessing] = useState(false);

  const tt = (en, es) => language === 'es' ? es : en;
  const isBuyer = user?.role === 'user';
  const currentTier = isBuyer ? (user?.buyer_tier || 'free') : (user?.agent_tier || 'free');

  // Fetch live pricing from the backend so Super Admin can edit prices without redeploys
  useEffect(() => {
    axios.get(`${API}/payments/config/public`)
      .then((r) => setSkus(r.data?.skus || {}))
      .catch(() => setSkus({}));
  }, []);

  // Resolve a price from the backend skus map; falls back to the default if config still loading
  const priceOf = (skuKey, fallback) => {
    if (!skus) return fallback;
    const cents = skus[skuKey]?.price_usd_cents;
    if (cents == null) return fallback;
    // Drop trailing .00 so "$29.00" → "$29"
    const dollars = cents / 100;
    const rounded = Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2);
    return `$${rounded}`;
  };

  const buyerPlans = [
    {
      id: 'free',
      name: tt('Free Buyer', 'Comprador Gratis'),
      price: '$0',
      period: tt('/month', '/mes'),
      icon: Star,
      color: 'border-slate-200 bg-white',
      badgeColor: 'bg-slate-100 text-slate-600',
      features: [
        tt('Browse all listings', 'Explorar todas las propiedades'),
        tt('Save favorites', 'Guardar favoritos'),
        tt('Send contact messages', 'Enviar mensajes de contacto'),
        tt('Book virtual tours', 'Reservar tours virtuales'),
      ],
    },
    {
      id: 'premium',
      name: tt('Premium Buyer', 'Comprador Premium'),
      price: priceOf('premium_buyer', '$9'),
      period: tt('/month', '/mes'),
      icon: Sparkles,
      color: 'border-[#D4AF37] bg-[#D4AF37]/5',
      badgeColor: 'bg-[#D4AF37]/20 text-[#0B192C]',
      popular: true,
      features: [
        tt('Everything in Free', 'Todo lo del plan Gratis'),
        tt('Direct agent email & phone access', 'Acceso directo a correo y teléfono del agente'),
        tt('View full agent profiles', 'Ver perfiles completos de agentes'),
        tt('Priority tour scheduling', 'Reserva prioritaria de tours'),
        tt('Verified agents only filter', 'Filtro solo agentes verificados'),
      ],
    },
  ];

  const agentPlans = [
    {
      id: 'free',
      name: tt('Free', 'Gratis'),
      price: '$0',
      period: tt('/month', '/mes'),
      icon: Star,
      color: 'border-slate-200 bg-white',
      badgeColor: 'bg-slate-100 text-slate-600',
      features: [
        tt('2 active property listings', '2 propiedades activas'),
        tt('Basic dashboard', 'Panel basico'),
        tt('Contact messages', 'Mensajes de contacto'),
        tt('Property analytics', 'Analisis de propiedades'),
      ],
    },
    {
      id: 'independent',
      name: tt('Independent', 'Independiente'),
      price: priceOf('agent_independent', '$29'),
      period: tt('/month', '/mes'),
      icon: Zap,
      color: 'border-blue-300 bg-blue-50/30',
      badgeColor: 'bg-blue-100 text-blue-700',
      popular: true,
      features: [
        tt('12 active property listings', '12 propiedades activas'),
        tt('Advanced dashboard & analytics', 'Panel avanzado y analisis'),
        tt('Contact user profiles', 'Perfiles de contactos'),
        tt('Transaction management', 'Gestion de transacciones'),
        tt('Priority support', 'Soporte prioritario'),
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      price: priceOf('agent_pro', '$79'),
      period: tt('/month', '/mes'),
      icon: Crown,
      color: 'border-purple-300 bg-purple-50/30',
      badgeColor: 'bg-purple-100 text-purple-700',
      features: [
        tt('Unlimited property listings', 'Propiedades ilimitadas'),
        tt('Team management (sub-accounts)', 'Gestion de equipo (sub-cuentas)'),
        tt('Corporate branding & logo', 'Marca corporativa y logo'),
        tt('Conversion analytics', 'Analisis de conversion'),
        tt('Featured listing credits', 'Creditos de propiedades destacadas'),
        tt('Dedicated account manager', 'Gerente de cuenta dedicado'),
      ],
    },
  ];

  const plans = isBuyer ? buyerPlans : agentPlans;
  const backLink = isBuyer ? '/dashboard' : '/agency';

  const handleUpgrade = async (planId) => {
    if (planId === currentTier || planId === 'free') return;
    const mapping = PLAN_SKU_MAP[planId];
    if (!mapping) {
      toast.error(tt('Unknown plan', 'Plan desconocido'));
      return;
    }
    // Always route to the manual-payment checkout (Bank Transfer / Pago Móvil / Zelle)
    setProcessing(true);
    try {
      navigate(`/payments/checkout?sku=${mapping.sku}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="upgrade-page">
      <Link to={backLink} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#D4AF37] mb-4">
        <ArrowLeft className="w-4 h-4" />{tt('Back to Dashboard', 'Volver al Panel')}
      </Link>

      <div className="text-center mb-8">
        <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-2">{tt('Pricing', 'Precios')}</p>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          {isBuyer ? tt('Unlock Premium Buyer Access', 'Desbloquea Acceso Comprador Premium') : tt('Choose Your Plan', 'Elige Tu Plan')}
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-lg mx-auto">
          {isBuyer
            ? tt('Get verified agent contact details and connect directly with sellers.', 'Obtén los datos de contacto de agentes verificados y conecta directamente con vendedores.')
            : tt('Unlock more listings, advanced tools, and grow your real estate business in Venezuela.', 'Desbloquea mas listados, herramientas avanzadas y haz crecer tu negocio inmobiliario en Venezuela.')}
        </p>
        {skus === null && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-400">
            <Loader2 className="w-3 h-3 animate-spin" />
            {tt('Loading live prices…', 'Cargando precios actualizados…')}
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 ${isBuyer ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'} gap-5`}>
        {plans.map(plan => {
          const Icon = plan.icon;
          const isCurrent = plan.id === currentTier;
          const isDowngrade = plans.findIndex(p => p.id === plan.id) < plans.findIndex(p => p.id === currentTier);
          const refreshUserRef = refreshUser; // silence eslint about unused
          void refreshUserRef;
          return (
            <Card key={plan.id} className={`relative overflow-hidden transition-shadow hover:shadow-lg ${plan.color} ${isCurrent ? 'ring-2 ring-primary' : ''}`}
              data-testid={`plan-${plan.id}`}>
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-[#D4AF37] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
                  {tt('POPULAR', 'POPULAR')}
                </div>
              )}
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-white border flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg">{plan.name}</h3>
                    <Badge className={plan.badgeColor + ' text-[10px]'}>
                      {isCurrent ? tt('Current Plan', 'Plan Actual') : plan.id.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                <div className="mb-5">
                  <span className="font-heading text-3xl font-bold text-slate-900" data-testid={`price-${plan.id}`}>{plan.price}</span>
                  <span className="text-sm text-slate-400">{plan.period}</span>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <Button disabled className="w-full" variant="outline" data-testid={`btn-${plan.id}`}>
                    {tt('Current Plan', 'Plan Actual')}
                  </Button>
                ) : isDowngrade ? (
                  <Button disabled className="w-full" variant="outline" data-testid={`btn-${plan.id}`}>
                    {tt('Downgrade', 'Bajar de Plan')}
                  </Button>
                ) : (
                  <Button
                    className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]"
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={processing}
                    data-testid={`btn-${plan.id}`}
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : tt('Upgrade', 'Mejorar')}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-400 mt-8 max-w-md mx-auto">
        {tt(
          'Pay with Bank Transfer, Pago Móvil or Zelle. Your subscription activates automatically once Admin approves the payment proof.',
          'Paga con Transferencia Bancaria, Pago Móvil o Zelle. Tu suscripción se activa automáticamente al aprobar el comprobante.',
        )}
      </p>

      <p className="text-center text-xs text-slate-400 mt-2">
        {tt('Logged in as', 'Sesión iniciada como')} <strong>{user?.email}</strong>
      </p>
      {token ? null : <span className="hidden">{token}</span>}
    </div>
  );
}
